import React from 'react';
import _ from 'lodash';
import qs from 'qs';
import { Button, Pill } from '@janus.team/janus-particles';
import { Badge, Form, Container } from 'react-bootstrap';
import { Link, useHistory } from 'react-router-dom';

import Drawer from 'common/drawer';
import { useParsedQueryString, useUpdatedTo } from 'projects/project-selector/hooks';
import { useProductModelContext } from 'customer/product-model-context';
import { serviceLevelLabel, getServiceBlockName, isServiceLevel } from '../utils';

// Query string values are always strings!
// ?x=false when parsed evaluates to { x: 'false' }, which is a truthy value for x (the string "false")
// Using 1/0 (string) as true/false hopefully reduces confusion
const TRUE = '1';
const FALSE = '0';

const filterTypes = {
  STATUS: 'status',
  SERVICE_BLOCKS: 'serviceBlocks',
};

const getActiveFilters = (filter) => ({
  status: _.pickBy(filter?.status, (entry) => (entry === TRUE)),
  serviceBlocks: _.pickBy(filter?.serviceBlocks, (entry) => (entry === TRUE)),
});

export const emptySelectionFrom = ({ serviceBlocks, statuses }) => ({
  serviceBlocks: _.reduce(serviceBlocks, (result, id) => ({ ...result, [id]: FALSE }), {}),
  status: _.reduce(statuses, (result, id) => ({ ...result, [id]: FALSE }), {}),
});

export const matchesServiceBlocksFilter = ({ filter, project }) => {
  const filterServiceBlocks = _.pickBy(filter?.serviceBlocks, (value) => (value === '1'));
  return (!_.some(filterServiceBlocks) || _.some(filterServiceBlocks, (__, serviceBlock) => (
    _.includes(_.concat(
      [project?.serviceLevel],
      _.map(project?.serviceBlocks, 'serviceBlockId'),
    ),
    serviceBlock)
  )));
};

export const matchesStatusFilter = ({ filter, project }) => {
  const filterStatus = _.pickBy(filter?.status, (value) => (value === '1'));
  return (!_.some(filterStatus) || _.some(filterStatus, (__, status) => (
    _.includes(_.concat(
      [project?.linkedState],
    ), status)
  )));
};

export const matchesFilters = (project, filter) => matchesStatusFilter({ filter, project }) && matchesServiceBlocksFilter({ filter, project });

const FilterPill = ({ type, text, action }) => (
  <Pill color="none" filled={false} square>
    <div className="d-flex justify-content-center align-items-center">
      <strong>{type}</strong>
      {`: ${text}`}
      <Button
        className="px-0 py-0 btn-close"
        type="button"
        variant="link"
        style={{ marginLeft: '10px', height: '8px' }}
        onClick={action}
      />
    </div>
  </Pill>
);

export const ActiveFiltersPills = ({ filterState, setFilterState }) => {
  const { products } = useProductModelContext();
  const history = useHistory();

  const removeFromFilter = (filterType, filterValue) => {
    const updatedFilter = { ...filterState };
    updatedFilter[filterType][filterValue] = FALSE;
    return getActiveFilters(updatedFilter);
  };
  // Updates filterState and removes filter from URL query-string
  const clearFilterAction = (filterType, filterValue) => () => {
    const newFilter = removeFromFilter(filterType, filterValue);
    setFilterState((previous) => _.merge({}, previous, {
      [filterType]: { [filterValue]: FALSE },
    }));
    history.replace({
      search: qs.stringify({ filter: newFilter }),
    });
  };
  const clearAllFilters = () => {
    setFilterState({ status: {}, serviceBlocks: {} });
    history.replace({ search: '' });
  };

  const activeFilters = getActiveFilters(filterState);
  const statusFilters = _.map(activeFilters?.status, (isActive, projectStatus) => {
    const action = clearFilterAction(filterTypes.STATUS, projectStatus);
    return (
      <FilterPill key={projectStatus} type="Status" text={_.lowerCase(projectStatus)} action={action} />
    );
  });
  const serviceBlocksFilters = _.map(activeFilters?.serviceBlocks, (isActive, serviceId) => {
    const [type, text] = isServiceLevel(serviceId)
      ? ['Service Level', serviceLevelLabel(serviceId)]
      : ['Service Block', getServiceBlockName(products, serviceId)];
    const action = clearFilterAction(filterTypes.SERVICE_BLOCKS, serviceId);
    return (
      <FilterPill key={serviceId} type={type} text={text} action={action} />
    );
  });

  if (_.isEmpty(serviceBlocksFilters) && _.isEmpty(statusFilters)) {
    return null;
  }

  return (
    <Container className="d-flex flex-wrap gap-4 align-items-center">
      { statusFilters }
      { serviceBlocksFilters }
      <Button
        className="px-0 text-decoration-underline font-weight-bolder border-0"
        type="button"
        variant="link"
        onClick={clearAllFilters}
      >
        <strong>Clear all filters</strong>
      </Button>
    </Container>
  );
};

const FilterEntry = ({
  name, value, setFilterState, prefix, filterState, count,
}) => {
  const checked = !!(_.get(filterState, [prefix, value]) === TRUE);
  const toggle = ({ target: { name, checked, value } }) => {
    setFilterState((previous) => _.merge({}, previous, {
      [name]: { [value]: checked ? TRUE : FALSE },
    }));
  };

  return (
    <Form.Check id={`filter-${_.kebabCase(name)}`} className="rax-mgcp-project-filter-checkbox">
      <Form.Check.Input checked={checked} onChange={toggle} name={prefix} value={value} />
      <Form.Check.Label className="w-100 d-flex justify-content-between">
        <div>{name}</div>
        <Badge className="d-block badge-gray500" bg="" pill>{count || 0}</Badge>
      </Form.Check.Label>
    </Form.Check>
  );
};

export const ProjectFilterDrawer = ({
  filterState, setFilterState, projects, serviceBlockOptions, statusOptions,
}) => {
  const queryString = useParsedQueryString() || {};
  const isOpen = queryString?.filterDrawer === 'open';
  const toClosedFilter = useUpdatedTo({ filterDrawer: undefined });
  const history = useHistory();
  const close = () => {
    setFilterState(queryString?.filter);
    history.replace(toClosedFilter);
  };
  const filteredProjects = projects.filter((project) => matchesFilters(project, filterState));
  const toAppliedFilters = useUpdatedTo({
    filterDrawer: undefined,
    filter: getActiveFilters(filterState),
  });

  const selectNothing = () => setFilterState(
    emptySelectionFrom({ serviceBlocks: _.keys(serviceBlockOptions), statuses: _.keys(statusOptions) }),
  );

  return (
    <Drawer show={isOpen} handleClick={close} title="Filter projects">
      <div className="rax-mgcp-project-filter">
        <header className="rax-mgcp-project-filter__header container d-flex justify-content-between align-items-center border-bottom pb-6">
          <span>{`${filteredProjects.length} of ${projects.length} results`}</span>
          <Button className="px-0 text-decoration-underline border-0" type="button" variant="link" onClick={selectNothing}>Clear all</Button>
        </header>

        <div className="rax-mgcp-project-filter__body container py-8">
          <h3 className="mb-6">Service Blocks</h3>
          <div className="mb-8">
            {_.map(serviceBlockOptions, (serviceBlock) => <FilterEntry count={serviceBlock?.count} key={serviceBlock?.id} value={serviceBlock?.id} prefix="serviceBlocks" name={serviceBlock?.name} filterState={filterState} setFilterState={setFilterState} />)}
          </div>
          <h3 className="mb-6">Status</h3>
          <div className="mb-6">
            {_.map(statusOptions, (status) => <FilterEntry count={status.count} key={status?.id} value={status?.id} prefix="status" name={status?.name} filterState={filterState} setFilterState={setFilterState} />)}
          </div>
        </div>

        <footer className="rax-mgcp-project-filter__footer container border-top pt-6 d-flex">
          <Button className="particles-button--primary" as={Link} to={toAppliedFilters}>Apply filters</Button>
          <Button className="px-0 text-decoration-underline border-0" type="button" variant="link" onClick={close}>Cancel</Button>
        </footer>
      </div>
    </Drawer>
  );
};
